import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiZap, FiImage } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Card, DataTable, Button, Badge, ConfirmDialog, Toggle, Modal, ImageUpload } from '../../components/common';
import { projectService, projectImageUrl } from '../../services/projectService';
import ReviewFormInput from '../reviews/ReviewFormInput';
import { useModal, useForm } from '../../hooks';
import { validators } from '../../utils/validators';
import './ProjectsPage.css';

// Offered as suggestions only; any other category can be typed.
const DEFAULT_CATEGORIES = ['Ground-mount solar', 'Rooftop solar', 'Substation', 'Transmission line'];

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const formModal = useModal();
  const deleteModal = useModal();
  const [saving, setSaving] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Mirrors the API: only the title is required.
  const validationRules = {
    title: [validators.required, validators.minLength(2)],
  };

  const form = useForm({
    title: '',
    clientName: '',
    location: '',
    category: '',
    capacity: '',
    year: '',
    description: '',
    image: null,
    isFeatured: false,
    isActive: true,
  }, validationRules);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const response = await projectService.getAll();
      setProjects(response.data.projects);
    } catch (error) {
      toast.error('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // The website builds its filter from these, so suggesting what is already in
  // use keeps "Rooftop solar" from also appearing as "Rooftop".
  const categorySuggestions = useMemo(() => {
    const used = projects.map((p) => p.category.trim()).filter(Boolean);
    return [...new Set([...used, ...DEFAULT_CATEGORIES])];
  }, [projects]);

  // The endpoint has no `search` param and DataTable only reports the typed
  // value, so the filtering happens here. Nothing is requested, so no debounce.
  const visibleProjects = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return projects;
    return projects.filter((p) =>
      `${p.title} ${p.clientName} ${p.location} ${p.category}`.toLowerCase().includes(term)
    );
  }, [projects, search]);

  const featuredCount = projects.filter((p) => p.isFeatured && p.isActive).length;

  /**
   * Toast why a request failed. A 404 means the project was deleted after this
   * list loaded (another tab or user), so the list is refreshed rather than
   * leaving a row that can never be saved.
   */
  const reportError = (error, fallback) => {
    const { status, data } = error.response ?? {};
    if (status === 404) {
      toast.error('This project no longer exists. The list has been refreshed.');
      formModal.close();
      fetchProjects();
    } else if (status === 422 && data?.errors) {
      toast.error(Object.values(data.errors).flat()[0] || fallback);
    } else if (status !== 500) {
      // 500s are already toasted by the api interceptor.
      toast.error(fallback);
    }
  };

  const handleOpenForm = (project = null) => {
    if (project) {
      form.setValues({
        id: project.id,
        title: project.title,
        clientName: project.clientName,
        location: project.location,
        category: project.category,
        capacity: project.capacity,
        year: project.year,
        description: project.description,
        // A string URL rather than a File, so buildFormData skips it and the
        // existing photo survives a save that does not touch it.
        image: projectImageUrl(project.image),
        isFeatured: project.isFeatured,
        isActive: project.isActive,
      });
    } else {
      form.reset();
    }
    formModal.open(project);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.validate()) {
      toast.error('Please fix the form errors');
      return;
    }

    setSaving(true);
    try {
      // ImageUpload hands back { file, preview } for a new upload; an untouched
      // existing photo is still the string URL set above.
      const payload = {
        title: form.values.title,
        clientName: form.values.clientName,
        location: form.values.location,
        category: form.values.category.trim(),
        capacity: form.values.capacity,
        year: form.values.year,
        description: form.values.description,
        isFeatured: form.values.isFeatured,
        isActive: form.values.isActive,
        image: form.values.image?.file || form.values.image,
      };

      if (formModal.data) {
        await projectService.update(formModal.data.id, payload);
        toast.success('Project updated successfully');
      } else {
        await projectService.create(payload);
        toast.success('Project added successfully');
      }

      formModal.close();
      fetchProjects();
    } catch (error) {
      reportError(error, 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id, field) => {
    try {
      if (field === 'isFeatured') {
        await projectService.toggleFeatured(id);
      } else {
        await projectService.toggleStatus(id);
      }
      setProjects(prev => prev.map(item =>
        item.id === id ? { ...item, [field]: !item[field] } : item
      ));
      toast.success(field === 'isFeatured' ? 'Home page listing updated' : 'Status updated successfully');
    } catch (error) {
      reportError(error, 'Failed to update project');
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await projectService.delete(deleteModal.data);
      toast.success('Project deleted successfully');
      fetchProjects();
    } catch (error) {
      reportError(error, 'Failed to delete project');
    } finally {
      setDeleteLoading(false);
      deleteModal.close();
    }
  };

  const fillDemoData = () => {
    form.setValues({
      ...form.values,
      title: '50 MWp Ground-Mount Solar Plant',
      clientName: 'Adani Green Energy',
      location: 'Bhuj, Gujarat',
      category: 'Ground-mount solar',
      capacity: '50 MWp',
      year: '2024',
      description: 'Complete detailed engineering for a 50 MWp ground-mounted plant: PV layout, DC and AC design, earthing, lightning protection and the 33/220 kV pooling substation.',
      isFeatured: true,
      isActive: true,
    });
    toast.info('Demo data filled');
  };

  const columns = [
    {
      header: 'Project',
      // accessor kept alongside render so the CSV export is not blank.
      accessor: 'title',
      render: (row) => (
        <div className="project-cell">
          {row.image ? (
            <img src={projectImageUrl(row.image)} alt={row.title} className="project-thumb" />
          ) : (
            <span className="project-thumb project-thumb-empty"><FiImage /></span>
          )}
          <div className="project-identity">
            <span className="project-title">{row.title}</span>
            <span className="project-meta">
              {[row.clientName, row.location].filter(Boolean).join(' · ') || '-'}
            </span>
          </div>
        </div>
      )
    },
    {
      header: 'Category',
      accessor: 'category',
      // A dash, not a blank cell — in a dense table blank reads as "broken".
      render: (row) => row.category || '-'
    },
    {
      header: 'Capacity',
      accessor: 'capacity',
      render: (row) => row.capacity || '-'
    },
    {
      header: 'Year',
      accessor: 'year',
      width: '90px',
      render: (row) => row.year || '-'
    },
    {
      header: 'Home Page',
      width: '110px',
      render: (row) => (
        <Toggle
          name={`featured-${row.id}`}
          checked={row.isFeatured}
          onChange={() => handleToggle(row.id, 'isFeatured')}
          size="small"
        />
      )
    },
    {
      header: 'Status',
      width: '100px',
      render: (row) => (
        <Toggle
          name={`status-${row.id}`}
          checked={row.isActive}
          onChange={() => handleToggle(row.id, 'isActive')}
          size="small"
        />
      )
    },
    {
      header: 'State',
      width: '100px',
      render: (row) => (
        <Badge variant={row.isActive ? 'success' : 'warning'}>
          {row.isActive ? 'Active' : 'Inactive'}
        </Badge>
      )
    },
    {
      header: 'Actions',
      width: '100px',
      align: 'center',
      render: (row) => (
        <div className="action-buttons">
          <button
            className="action-btn edit"
            title="Edit"
            onClick={() => handleOpenForm(row)}
          >
            <FiEdit2 />
          </button>
          <button
            className="action-btn delete"
            title="Delete"
            onClick={() => deleteModal.open(row.id)}
          >
            <FiTrash2 />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="projects-page">
      <Card
        title="Our Projects"
        subtitle={`${projects.length} projects · ${featuredCount} on the home page`}
        action={
          <Button icon={<FiPlus />} onClick={() => handleOpenForm()}>
            Add Project
          </Button>
        }
      >
        <DataTable
          columns={columns}
          data={visibleProjects}
          loading={loading}
          searchable
          searchPlaceholder="Search projects..."
          searchValue={search}
          onSearch={setSearch}
          exportable
          exportFilename="projects-export"
          emptyMessage="No projects found"
        />
      </Card>

      {/* Form Modal */}
      <Modal
        isOpen={formModal.isOpen}
        onClose={formModal.close}
        title={formModal.data ? 'Edit Project' : 'Add Project'}
        size="large"
        footer={
          <>
            <Button variant="ghost" onClick={formModal.close}>
              Cancel
            </Button>
            <Button variant="secondary" icon={<FiZap />} onClick={fillDemoData} type="button">
              Fill Demo Data
            </Button>
            <Button onClick={handleSubmit} loading={saving}>
              {formModal.data ? 'Update' : 'Save'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="project-form">
          <ReviewFormInput
            name="title"
            label="Project Title"
            placeholder="e.g. 50 MWp Ground-Mount Solar Plant"
            value={form.values.title}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            error={form.errors.title}
            touched={form.touched.title}
            required
          />
          {/* Free text on purpose — the client writes what should appear on the
              card. The placeholders are the only cue about the format. */}
          <div className="project-form-row">
            <ReviewFormInput
              name="clientName"
              label="Client (optional)"
              placeholder="e.g. Adani Green Energy"
              value={form.values.clientName}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
            />
            <ReviewFormInput
              name="location"
              label="Location (optional)"
              placeholder="e.g. Bhuj, Gujarat"
              value={form.values.location}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
            />
          </div>
          <div className="project-form-row project-form-row-3">
            <ReviewFormInput
              name="category"
              label="Category (optional)"
              placeholder="e.g. Rooftop solar"
              value={form.values.category}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              list="project-categories"
            />
            <ReviewFormInput
              name="capacity"
              label="Capacity (optional)"
              placeholder="e.g. 50 MWp"
              value={form.values.capacity}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
            />
            <ReviewFormInput
              name="year"
              label="Year (optional)"
              placeholder="e.g. 2024 or Ongoing"
              value={form.values.year}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
            />
          </div>
          <datalist id="project-categories">
            {categorySuggestions.map((c) => <option key={c} value={c} />)}
          </datalist>
          <ReviewFormInput
            name="description"
            label="Description (optional)"
            placeholder="Scope of work, highlights, what was delivered..."
            value={form.values.description}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            textarea
            rows={4}
          />
          <ImageUpload
            value={form.values.image}
            onChange={(image) => form.setFieldValue('image', image)}
            label="Project Photo (optional)"
          />
          <div className="project-form-toggles">
            <Toggle
              name="isFeatured"
              checked={form.values.isFeatured}
              onChange={(e) => form.setFieldValue('isFeatured', e.target.checked)}
              label="Show on home page"
            />
            <Toggle
              name="isActive"
              checked={form.values.isActive}
              onChange={(e) => form.setFieldValue('isActive', e.target.checked)}
              label="Active"
            />
          </div>
          <p className="project-form-hint">
            Only active projects appear on the website. The home page shows projects marked
            &ldquo;Show on home page&rdquo;; if none are, it shows the three most recent.
          </p>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        onConfirm={handleDelete}
        title="Delete Project"
        message="Are you sure you want to delete this project? This action cannot be undone."
        confirmText="Delete"
        loading={deleteLoading}
      />
    </div>
  );
};

export default ProjectsPage;
